import React, { memo } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { makeStyles, Typography } from 'transfix-ui/core'
import { Table, TableHead, TableBody, TableCell, TableRow } from 'transfix-ui'
import { IIssueAutomationRuleFragment } from '../graphql/IssueAutomationRuleFragment'
import IssueRuleRow from './IssueRuleRow'

interface IIssueRulesTableProps {
  rules: IIssueAutomationRuleFragment[]
  onRename: (ruleId: string) => void
  onDelete: (ruleId: string) => void
  onUpdate: (ruleId: string) => void
  onNewRule: () => void
  selectedRuleId: string | null
  canEdit: boolean
}

enum TABLE_COLUMNS {
  name = 'Name',
  category = 'Rule Type',
  description = 'Last Updated by',
  edit = '',
}

const useStyles = makeStyles(theme => ({
  emptyState: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '& > p': {
      padding: 0,
    },
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  tableHeaderRow: {
    '& > th:first-child': {
      width: '35%',
    },
  },
}))

const IssueRulesTable = memo(
  ({
    canEdit,
    onDelete,
    onNewRule,
    onRename,
    onUpdate,
    rules,
    selectedRuleId,
  }: IIssueRulesTableProps) => {
    const classes = useStyles()

    return (
      <div>
        <Table className={classes.table}>
          <TableHead>
            <TableRow tableCellType='th' className={classes.tableHeaderRow}>
              {Object.entries(TABLE_COLUMNS).map(([key, value]) => (
                <TableCell
                  tableCellType='th'
                  key={`${key}-column-header`}
                  dataTest={`${key}-column-header`}>
                  {value}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody dataTest='control-panel-table'>
            {rules.map(rule => (
              <IssueRuleRow
                key={rule.id}
                onRename={onRename}
                onDelete={onDelete}
                onUpdate={onUpdate}
                rule={rule}
                selectedRuleId={selectedRuleId}
              />
            ))}
          </TableBody>
        </Table>
        {!rules?.length && (
          <div className={classes.emptyState}>
            <Typography>No Rules Found</Typography>
            {canEdit && (
              <Typography variant='body2'>
                Click{' '}
                <Button variant='text' noPadding rel='noopener noreferrer' onClick={onNewRule}>
                  Add New Rule
                </Button>{' '}
                to create rules for this issue type
              </Typography>
            )}
          </div>
        )}
      </div>
    )
  }
)

export default IssueRulesTable
